import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Select,
  SelectItem,
} from '@nextui-org/react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { COLORS } from 'common/src/utils/colors';
import { getSettlementsFobVsExporters } from 'common/src/api/settlements';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import ModalSlideFilter from '../../../../../common/src/components/filters/modal-sidebar-filter';
import { Button } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import * as XLSX from 'xlsx';
import { logUserAction } from '../../../services/actionsService';
import useFilterStore from '../../../../../common/src/stores/useFiltersStore';
import SeasonFilter from '../../../../../common/src/components/filters/SeasonFilter';
import MethologyFobExportersModal from '../../../components/modals/MethologyFobExportersModal';
import Header from '../../../components/layouts/Header';
import {
  Alert,
  EmptyState,
  FiltersToggle,
  LoadingState,
  PageLayout,
} from '../../../components/ui';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import { getSeasonWeeksForSpecies } from 'common/src/utils/seasonWeeks';

interface BoxPlotData {
  own: number | null;
  others: {
    min: number;
    q1: number;
    median: number;
    q3: number;
    max: number;
  };
}

interface WeekData {
  [caliber: string]: BoxPlotData;
}

interface VarietyData {
  [week: string]: WeekData;
}

interface ComparativeData {
  [variety: string]: VarietyData;
}

const SettlementsComparativeExporters = () => {
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const [data, setData] = useState<ComparativeData>({});
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState<number>(2);
  const [selectedVariety, setSelectedVariety] = useState<string>('LAPINS');
  const [varieties, setVarieties] = useState<string[]>([]);
  const { session, userProfile, darkMode } = useUserStore();
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [priceView, setPriceView] = useState<'kg' | 'box'>('kg');
  const [visualization, setVisualization] = useState<'chart' | 'table'>(
    'chart'
  );
  const [selectedCaliber, setSelectedCaliber] = useState<string>('2J');
  const [methodology, setMethodology] = useState<'by_volume' | 'by_exporter'>(
    'by_exporter'
  );
  const [dateMethodology, setDateMethodology] = useState<
    'date_sold' | 'eta' | 'etd'
  >('eta');
  const [isMethodologyModalOpen, setIsMethodologyModalOpen] = useState(false);
  const [sinisterFilter, setSinisterFilter] = useState<
    'all' | 'sinister' | 'non_sinister'
  >('all');
  const [separation, setSeparation] = useState<
    'none' | 'by_label' | 'by_receiver'
  >('none');
  const weeks = useMemo(
    () => getSeasonWeeksForSpecies(selectedSpecies?.id, selectedSeason),
    [selectedSpecies?.id, selectedSeason]
  );

  const calculatePercentile = (
    value: number | null,
    boxPlotData: {
      min: number;
      q1: number;
      median: number;
      q3: number;
      max: number;
    }
  ) => {
    if (!value) return null;
    if (value >= boxPlotData.max) return 100;
    if (value <= boxPlotData.min) return 0;

    // Calculate which quartile the value falls into
    if (value <= boxPlotData.q1) {
      // Between min and Q1 (0-25%)
      return Math.round(
        ((value - boxPlotData.min) / (boxPlotData.q1 - boxPlotData.min)) * 25
      );
    } else if (value <= boxPlotData.median) {
      // Between Q1 and median (25-50%)
      return Math.round(
        25 +
          ((value - boxPlotData.q1) / (boxPlotData.median - boxPlotData.q1)) *
            25
      );
    } else if (value <= boxPlotData.q3) {
      // Between median and Q3 (50-75%)
      return Math.round(
        50 +
          ((value - boxPlotData.median) /
            (boxPlotData.q3 - boxPlotData.median)) *
            25
      );
    } else {
      // Between Q3 and max (75-100%)
      return Math.round(
        75 +
          ((value - boxPlotData.q3) / (boxPlotData.max - boxPlotData.q3)) * 25
      );
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Skip if not cherry
        if (selectedSpecies?.id !== 2) {
          setLoading(false);
          return;
        }

        const weekNumber = selectedWeek;
        const data = await getSettlementsFobVsExporters(
          session,
          methodology,
          selectedSeason,
          selectedSpecies?.id,
          sinisterFilter,
          separation,
          weekNumber,
          null,
          dateMethodology
        );

        if (!data) {
          throw new Error('No response received from server');
        }

        setData(data);

        // Get unique varieties
        const allVarieties = new Set<string>();
        Object.entries(data).forEach(([variety]) => {
          allVarieties.add(variety);
        });

        const varietiesArray = Array.from(allVarieties);
        setVarieties(varietiesArray);

        // Set defaults based on whether it's a demo user or not
        if (userProfile?.exporter?.hasDemo) {
          // For demo users, prefer LAPINS if available, otherwise first available
          const preferredVariety = varietiesArray.includes('LAPINS')
            ? 'LAPINS'
            : varietiesArray[0] || '';
          setSelectedVariety(preferredVariety);
          setSelectedCaliber('2J');
        } else {
          // Try to keep the previously selected variety if it exists in the new data
          if (selectedVariety && varietiesArray.includes(selectedVariety)) {
            // Keep the current selection
          } else {
            // Otherwise prefer LAPINS if available, else select the first available variety
            const preferredVariety = varietiesArray.includes('LAPINS')
              ? 'LAPINS'
              : varietiesArray[0] || '';
            setSelectedVariety(preferredVariety);
          }
          setSelectedCaliber('2J');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (session && selectedSpecies?.id) {
      fetchData();
    }
  }, [
    methodology,
    selectedSeason,
    sinisterFilter,
    separation,
    selectedWeek,
    dateMethodology,
    selectedSpecies?.id,
  ]);

  useEffect(() => {
    logUserAction('view', 'page', 'settlements-comparative-exporters');
  }, []);

  // Add these shared chart options
  const getBaseChartOptions = (
    xAxisTitle: string,
    categories: string[],
    priceView: 'kg' | 'box'
  ): ApexOptions => ({
    chart: {
      type: 'boxPlot',
      height: 600,
      foreColor: darkMode ? '#A1A1AA' : '#71717A',
      toolbar: { show: true },
      zoom: { enabled: false },
      animations: { enabled: true },
    },
    colors: [
      COLORS.cherry,
      COLORS.lightGreen,
      COLORS.blue,
      COLORS.gray,
      COLORS.yellow,
    ],
    plotOptions: {
      boxPlot: {
        colors: {
          upper: COLORS.cherry,
          lower: COLORS.cherry,
        },
      },
    },
    xaxis: {
      type: 'category',
      categories,
      title: { text: xAxisTitle },
      crosshairs: {
        show: true,
        width: 1,
        opacity: 0.9,
        stroke: {
          color: '#b6b6b6',
          width: 1,
          dashArray: 0,
        },
        fill: {
          type: 'solid',
          color: '#B1B9C4',
          gradient: {
            colorFrom: '#D8E3F0',
            colorTo: '#BED1E6',
            stops: [0, 100],
            opacityFrom: 0.4,
            opacityTo: 0.5,
          },
        },
        dropShadow: {
          enabled: false,
          top: 0,
          left: 0,
          blur: 1,
          opacity: 0.4,
        },
      },
      tooltip: {
        enabled: false, // Disable separate x-axis tooltip
      },
    },
    yaxis: {
      title: {
        text: priceView === 'kg' ? 'FOB USD/kg' : 'FOB USD/Caja',
      },
      labels: {
        formatter: (value) => `$${value ? value.toFixed(2) : 0}`,
      },
    },
    tooltip: {
      intersect: false,
      shared: true,
      fixed: {
        enabled: false,
      },
    },
    grid: {
      show: true,
      xaxis: {
        lines: { show: true },
      },
    },
  });

  // Create a shared tooltip generator
  const generateTooltipHtml = (
    caliber: string,
    currentData: any,
    priceView: 'kg' | 'box',
    separation: 'none' | 'by_label' | 'by_receiver',
    calculatePercentile: Function
  ) => {
    if (!currentData) return '';

    const ownValues = currentData?.own || [];
    const caliberOthersKg = currentData?.othersKg || 0;

    const ownValuesSection = ownValues
      .map((item) => {
        const label =
          separation === 'by_label'
            ? item.label
            : separation === 'by_receiver'
              ? item.receiver
              : 'Propio';
        const value = item.value;

        const percentile =
          caliberOthersKg > 0
            ? calculatePercentile(value, {
                min: currentData.others.min,
                q1: currentData.others.q1,
                median: currentData.others.median,
                q3: currentData.others.q3,
                max: currentData.others.max,
              })
            : null;

        return `
        <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
          <span style="color: #4CAF50; font-weight: bold;">${label}</span>
          <span style="font-weight: bold; color: #000000; margin-left: 10px;">$${
            !isNaN(Number(value))
              ? (value * (priceView === 'box' ? 5 : 1)).toFixed(2)
              : 'No definido'
          }</span>
        </div>
        ${
          percentile !== null
            ? `
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: #4CAF50; font-weight: bold;">Percentil</span>
            <span style="font-weight: bold; color: #000000; margin-left: 10px;">${
              percentile >= 0 ? percentile : 'No definido'
            }</span>
          </div>
        `
            : ''
        }
        <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
          <span style="color: #4CAF50; font-weight: bold;">Kilos</span>
          <span style="font-weight: bold; color: #000000; margin-left: 10px;">${item.kilos.toLocaleString(
            'es-CL'
          )} kg</span>
        </div>
        ${
          ownValues.length > 1
            ? '<div style="border-top: 1px solid #eee; margin: 8px 0;"></div>'
            : ''
        }
      `;
      })
      .join('');

    const boxPlotSection =
      caliberOthersKg > 0
        ? `
      <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
        <span style="color: #666;">Máximo:</span>
        <span style="font-weight: bold; color: #000000; margin-left: 10px;">$${
          currentData.others.max
            ? (currentData.others.max * (priceView === 'box' ? 5 : 1)).toFixed(
                2
              )
            : 'No definido'
        }</span>
      </div>
      <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
        <span style="color: #666;">Q3:</span>
        <span style="font-weight: bold; color: #000000; margin-left: 10px;">$${
          currentData.others.q3
            ? (currentData.others.q3 * (priceView === 'box' ? 5 : 1)).toFixed(2)
            : 'No definido'
        }</span>
      </div>
      <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
        <span style="color: #666;">Mediana:</span>
        <span style="font-weight: bold; color: #000000; margin-left: 10px;">$${
          currentData.others.median
            ? (
                currentData.others.median * (priceView === 'box' ? 5 : 1)
              ).toFixed(2)
            : 'No definido'
        }</span>
      </div>
      <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
        <span style="color: #666;">Q1:</span>
        <span style="font-weight: bold; color: #000000; margin-left: 10px;">$${
          currentData.others.q1
            ? (currentData.others.q1 * (priceView === 'box' ? 5 : 1)).toFixed(2)
            : 'No definido'
        }</span>
      </div>
      <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
        <span style="color: #666;">Mínimo:</span>
        <span style="font-weight: bold; color: #000000; margin-left: 10px;">$${
          currentData.others.min
            ? (currentData.others.min * (priceView === 'box' ? 5 : 1)).toFixed(
                2
              )
            : 'No definido'
        }</span>
      </div>
    `
        : '';

    return `
      <div style="background-color: #f8f9fa; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); padding: 12px; font-family: Arial, sans-serif;">
        <div style="text-align: center; font-size: 16px; font-weight: bold; color: #333; margin-bottom: 2px;">
          ${caliber}
        </div>
        ${boxPlotSection}
        ${
          ownValues.length > 0
            ? `
          ${
            boxPlotSection
              ? '<div style="border-top: 1px solid #eee; margin: 8px 0;"></div>'
              : ''
          }
          ${ownValuesSection}
        `
            : `
          <div style="font-size: 12px; color: #666; text-align: center;">
            No hay datos propios para este calibre
          </div>
        `
        }
        ${
          caliberOthersKg > 0
            ? `
          <div style="border-top: 1px solid #eee; margin: 8px 0;"></div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: #666;">Kilos Totales Otras:</span>
            <span style="font-weight: bold; color: #000000; margin-left: 10px;">${caliberOthersKg.toLocaleString(
              'es-CL'
            )} kg</span>
          </div>
        `
            : ''
        }
      </div>
    `;
  };

  // Update the chart options to use shared base
  const getChartOptions = (
    type: 'caliber' | 'variety',
    categories: string[],
    priceView: 'kg' | 'box',
    data: any,
    selectedWeek: number,
    selectedVariety: string,
    selectedCaliber: string,
    separation: 'none' | 'by_label' | 'by_receiver'
  ): ApexOptions => {
    const baseOptions = getBaseChartOptions(
      type === 'caliber' ? 'Calibre' : 'Variedad',
      categories,
      priceView
    );

    return {
      ...baseOptions,
      tooltip: {
        shared: true,
        followCursor: true,
        custom: function ({ seriesIndex, dataPointIndex, w }) {
          const yearWeek =
            selectedWeek < 27
              ? `${selectedSeason.split('-')[1]}-W${selectedWeek}`
              : `${selectedSeason.split('-')[0]}-W${selectedWeek}`;
          const caliber = w.globals.categoryLabels[dataPointIndex];
          const currentData =
            type === 'caliber'
              ? data[selectedVariety]?.[yearWeek]?.[caliber]
              : data[caliber]?.[yearWeek]?.[selectedCaliber];

          return generateTooltipHtml(
            type === 'caliber' ? `Calibre ${caliber}` : caliber,
            currentData,
            priceView,
            separation,
            calculatePercentile
          );
        },
      },
    };
  };

  const prepareSeriesData = (type: 'caliber' | 'variety') => {
    if (!selectedWeek) return [];

    const categories =
      type === 'caliber' ? ['L', 'XL', 'J', '2J', '3J', '4J', '5J'] : varieties;

    const multiplier = priceView === 'box' ? 5 : 1;

    const yearWeek =
      selectedWeek < 27
        ? `${selectedSeason.split('-')[1]}-W${selectedWeek}`
        : `${selectedSeason.split('-')[0]}-W${selectedWeek}`;

    const boxPlotSeries = {
      name: 'OTRAS EXPORTADORAS',
      type: 'boxPlot',
      data: categories.map((category) => {
        const categoryData =
          type === 'caliber'
            ? data[selectedVariety]?.[yearWeek]?.[category]
            : data[category]?.[yearWeek]?.[selectedCaliber];

        return {
          x: category,
          y:
            categoryData?.others && categoryData.othersKg > 0
              ? [
                  categoryData.others.min * multiplier,
                  categoryData.others.q1 * multiplier,
                  categoryData.others.median * multiplier,
                  categoryData.others.q3 * multiplier,
                  categoryData.others.max * multiplier,
                ]
              : [null, null, null, null, null],
        };
      }),
    };

    // Generate scatter series for own data points
    const scatterSeries = [];

    categories.forEach((category) => {
      const categoryData =
        type === 'caliber'
          ? data[selectedVariety]?.[yearWeek]?.[category]
          : data[category]?.[yearWeek]?.[selectedCaliber];

      if (categoryData?.own) {
        categoryData.own.forEach((ownData) => {
          const seriesName =
            separation === 'by_label'
              ? ownData.label
              : separation === 'by_receiver'
                ? ownData.receiver
                : 'PROPIO';

          // Find existing series or create new one
          let series = scatterSeries.find((s) => s.name === seriesName);
          if (!series) {
            series = {
              name: seriesName,
              type: 'scatter',
              data: categories.map(() => ({ x: '', y: null })),
            };
            scatterSeries.push(series);
          }

          // Update the data point for this category
          const categoryIndex = categories.indexOf(category);
          if (categoryIndex !== -1) {
            series.data[categoryIndex] = {
              x: category,
              y: ownData.value * multiplier,
            };
          }
        });
      }
    });

    return [boxPlotSeries, ...scatterSeries];
  };

  const exportToExcel = (type: 'caliber' | 'variety') => {
    const multiplier = priceView === 'box' ? 5 : 1;
    const yearWeek =
      selectedWeek < 27
        ? `${selectedSeason.split('-')[1]}-W${selectedWeek}`
        : `${selectedSeason.split('-')[0]}-W${selectedWeek}`;
    let excelData: any[] = [];
    let fileName = '';

    if (type === 'caliber') {
      fileName = `FOB_por_Calibre_${selectedVariety}_${selectedWeek}.xlsx`;
      const calibers = ['L', 'XL', 'J', '2J', '3J', '4J', '5J'];
      const caliberData = data[selectedVariety]?.[yearWeek];

      // Create header row
      excelData.push(['Estadística', ...calibers]);

      // Create data rows for each statistic
      const statistics = [
        { key: 'own', label: 'FOB Propio' },
        { key: 'percentile', label: 'Percentil Propio' },
        { key: 'ownKg', label: 'Kilos Propio' },
        { key: 'max', label: 'Máximo' },
        { key: 'q3', label: 'Q3' },
        { key: 'median', label: 'Mediana' },
        { key: 'q1', label: 'Q1' },
        { key: 'min', label: 'Mínimo' },
        { key: 'othersKg', label: 'Kilos Otras' },
      ];

      statistics.forEach(({ key, label }) => {
        const rowData = [label];
        calibers.forEach((caliber) => {
          const cd = caliberData?.[caliber];
          let value = '-';

          if (cd) {
            if (key === 'own') {
              if (!cd.own || cd.own.length === 0) {
                value = '-';
              } else if (separation === 'none') {
                value = cd.own[0].value
                  ? `$${(cd.own[0].value * multiplier).toFixed(2)}`
                  : '-';
              } else {
                const values = cd.own
                  .map((item) => {
                    const label =
                      separation === 'by_label' ? item.label : item.receiver;
                    return `${label} $${(item.value * multiplier).toFixed(2)}`;
                  })
                  .join('\n');
                value = values || '-';
              }
            } else if (key === 'percentile') {
              if (!cd.own || cd.own.length === 0) {
                value = '-';
              } else {
                const percentiles = cd.own.map((item) => {
                  const percentile = calculatePercentile(item.value, {
                    min: cd.others.min,
                    q1: cd.others.q1,
                    median: cd.others.median,
                    q3: cd.others.q3,
                    max: cd.others.max,
                  });
                  const label =
                    separation === 'by_label'
                      ? `${item.label} `
                      : separation === 'by_receiver'
                        ? `${item.receiver} `
                        : '';
                  return percentile !== null ? `${label}${percentile}%` : '-';
                });
                value = percentiles.join('\n');
              }
            } else if (key === 'ownKg') {
              if (!cd.own || cd.own.length === 0) {
                value = '-';
              } else {
                const kgValues = cd.own.map((item) => {
                  const label =
                    separation === 'by_label'
                      ? `${item.label} `
                      : separation === 'by_receiver'
                        ? `${item.receiver} `
                        : '';
                  return `${label}${item.kilos?.toLocaleString('es-CL')} kg`;
                });
                value = kgValues.join('\n');
              }
            } else if (key === 'othersKg') {
              value = cd.othersKg
                ? `${cd.othersKg.toLocaleString('es-CL')} kg`
                : '-';
            } else {
              value = cd.others[key as keyof typeof cd.others]
                ? `$${(
                    cd.others[key as keyof typeof cd.others] * multiplier
                  ).toFixed(2)}`
                : '-';
            }
          }
          rowData.push(value);
        });
        excelData.push(rowData);
      });
    } else {
      fileName = `FOB_por_Variedad_${selectedCaliber}_${yearWeek}.xlsx`;
      const varietyData = varieties.map(
        (variety) => data[variety]?.[yearWeek]?.[selectedCaliber]
      );

      // Create header row
      excelData.push(['Estadística', ...varieties]);

      // Create data rows for each statistic
      const statistics = [
        { key: 'own', label: 'FOB Propio' },
        { key: 'percentile', label: 'Percentil Propio' },
        { key: 'ownKg', label: 'Kilos Propios' },
        { key: 'max', label: 'Máximo' },
        { key: 'q3', label: 'Q3' },
        { key: 'median', label: 'Mediana' },
        { key: 'q1', label: 'Q1' },
        { key: 'min', label: 'Mínimo' },
        { key: 'othersKg', label: 'Kilos Otras' },
      ];

      statistics.forEach(({ key, label }) => {
        const rowData = [label];
        varieties.forEach((variety) => {
          const vd = data[variety]?.[yearWeek]?.[selectedCaliber];
          let value = '-';

          if (vd) {
            if (key === 'own') {
              if (!vd.own || vd.own.length === 0) {
                value = '-';
              } else if (separation === 'none') {
                value = vd.own[0].value
                  ? `$${(vd.own[0].value * multiplier).toFixed(2)}`
                  : '-';
              } else {
                const values = vd.own
                  .map((item) => {
                    const label =
                      separation === 'by_label' ? item.label : item.receiver;
                    return `${label} $${(item.value * multiplier).toFixed(2)}`;
                  })
                  .join('\n');
                value = values || '-';
              }
            } else if (key === 'percentile') {
              if (!vd.own || vd.own.length === 0) {
                value = '-';
              } else {
                const percentiles = vd.own.map((item) => {
                  const percentile = calculatePercentile(item.value, {
                    min: vd.others.min,
                    q1: vd.others.q1,
                    median: vd.others.median,
                    q3: vd.others.q3,
                    max: vd.others.max,
                  });
                  const label =
                    separation === 'by_label'
                      ? `${item.label} `
                      : separation === 'by_receiver'
                        ? `${item.receiver} `
                        : '';
                  return percentile !== null ? `${label}${percentile}%` : '-';
                });
                value = percentiles.join('\n');
              }
            } else if (key === 'ownKg') {
              if (!vd.own || vd.own.length === 0) {
                value = '-';
              } else {
                const kgValues = vd.own.map((item) => {
                  const label =
                    separation === 'by_label'
                      ? `${item.label} `
                      : separation === 'by_receiver'
                        ? `${item.receiver} `
                        : '';
                  return `${label}${item.kilos?.toLocaleString('es-CL')} kg`;
                });
                value = kgValues.join('\n');
              }
            } else if (key === 'othersKg') {
              value = vd.othersKg
                ? `${vd.othersKg.toLocaleString('es-CL')} kg`
                : '-';
            } else {
              value = vd.others[key as keyof typeof vd.others]
                ? `$${(
                    vd.others[key as keyof typeof vd.others] * multiplier
                  ).toFixed(2)}`
                : '-';
            }
          }
          rowData.push(value);
        });
        excelData.push(rowData);
      });
    }

    // Apply some basic styling
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Set column widths
    const colWidths = Array(excelData[0].length).fill({ wch: 20 }); // Increased width for better readability
    colWidths[0] = { wch: 25 }; // Make first column wider for labels
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    XLSX.writeFile(wb, fileName);
  };

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      {/* Sticky Sidebar */}
      <ModalSlideFilter
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
      >
        <div className='bg-background p-4 pt-8 h-full overflow-y-auto'>
          <div className='space-y-4'>
            <div className='flex items-center justify-between'>
              <p className='text-sm text-default-500 mb-1'>Metodología</p>
              <Button
                isIconOnly
                size='sm'
                variant='light'
                onPress={() => setIsMethodologyModalOpen(true)}
              >
                <Icon icon='tabler:help-circle' className='text-default-400' />
              </Button>
            </div>

            <Select
              label='Ponderación'
              selectedKeys={[methodology]}
              onSelectionChange={(keys) =>
                setMethodology(
                  Array.from(keys)[0] as 'by_volume' | 'by_exporter'
                )
              }
              className='w-full mb-4'
              isDisabled={selectedSpecies?.id !== 2 || loading}
            >
              <SelectItem key='by_volume' value='by_volume'>
                Por Volumen
              </SelectItem>
              <SelectItem key='by_exporter' value='by_exporter'>
                Por Exportadora
              </SelectItem>
            </Select>

            <Select
              label='Fecha'
              selectedKeys={[dateMethodology]}
              onSelectionChange={(keys) =>
                setDateMethodology(
                  Array.from(keys)[0] as 'date_sold' | 'eta' | 'etd'
                )
              }
              className='w-full mb-4'
              isDisabled={selectedSpecies?.id !== 2 || loading}
            >
              <SelectItem key='date_sold' value='date_sold'>
                Fecha Venta Liquidación
              </SelectItem>
              <SelectItem key='eta' value='eta'>
                Fecha Arribo Nave
              </SelectItem>
              <SelectItem key='etd' value='etd'>
                Fecha Zarpe Nave
              </SelectItem>
            </Select>

            <p className='text-sm text-default-500 mb-1'>Filtros</p>
            <SeasonFilter disabled={selectedSpecies?.id !== 2 || loading} />
            <Select
              label='Semana'
              selectedKeys={selectedWeek ? [selectedWeek.toString()] : []}
              onSelectionChange={(keys) => {
                setSelectedWeek(parseInt(Array.from(keys)[0] as string));
              }}
              className='w-full'
              isDisabled={
                loading ||
                userProfile?.exporter?.hasDemo ||
                selectedSpecies?.id !== 2
              }
              description={
                userProfile?.exporter?.hasDemo
                  ? 'Contrata CherryMarket para ver todas las semanas'
                  : ''
              }
            >
              {weeks.map((week) => (
                <SelectItem key={week} value={week}>
                  {String(week)}
                </SelectItem>
              ))}
            </Select>
            <div className='flex gap-2 items-center'>
              <Select
                label='Filtro Siniestros'
                selectedKeys={[sinisterFilter]}
                onSelectionChange={(keys) =>
                  setSinisterFilter(
                    Array.from(keys)[0] as typeof sinisterFilter
                  )
                }
                className='w-full'
                isDisabled={selectedSpecies?.id !== 2 || loading}
              >
                <SelectItem key='all' value='all'>
                  Todos
                </SelectItem>
                <SelectItem key='sinister' value='sinister'>
                  Solo Siniestros
                </SelectItem>
                <SelectItem key='non_sinister' value='non_sinister'>
                  Sin Siniestros
                </SelectItem>
              </Select>
            </div>
            <p className='text-sm text-default-500 mb-1'>Unidades</p>
            <Select
              label='Unidad de precio'
              value={priceView}
              onChange={(e) => setPriceView(e.target.value as 'kg' | 'box')}
              defaultSelectedKeys={['kg']}
              className='w-full'
              isDisabled={selectedSpecies?.id !== 2 || loading}
            >
              <SelectItem key='kg' value='kg' textValue='Precio por kg'>
                Precio por kg
              </SelectItem>
              <SelectItem
                key='box'
                value='box'
                textValue='Precio por caja - 5kgs'
              >
                Precio por caja - 5kgs
              </SelectItem>
            </Select>

            <p className='text-sm text-default-500 mb-1'>Visualización</p>
            <Select
              label='Tipo de visualización'
              value={visualization}
              onChange={(e) =>
                setVisualization(e.target.value as 'chart' | 'table')
              }
              defaultSelectedKeys={['chart']}
              className='w-full'
              isDisabled={selectedSpecies?.id !== 2 || loading}
            >
              <SelectItem key='chart' value='chart' textValue='Gráficos'>
                Gráficos
              </SelectItem>
              <SelectItem key='table' value='table' textValue='Tablas'>
                Tablas
              </SelectItem>
            </Select>

            <Select
              label='Separación'
              description='Separa los datos propios por etiqueta o cliente'
              value={separation}
              onChange={(e) =>
                setSeparation(
                  e.target.value as 'none' | 'by_label' | 'by_receiver'
                )
              }
              defaultSelectedKeys={['none']}
              className='w-full'
              isDisabled={selectedSpecies?.id !== 2 || loading}
            >
              <SelectItem key='none' value='none' textValue='No Separar'>
                No Separar
              </SelectItem>
              <SelectItem
                key='by_label'
                value='by_label'
                textValue='Por Etiqueta'
              >
                Por Etiqueta
              </SelectItem>
              <SelectItem
                key='by_receiver'
                value='by_receiver'
                textValue='Por Cliente'
              >
                Por Cliente
              </SelectItem>
            </Select>
          </div>
        </div>
      </ModalSlideFilter>

      <MethologyFobExportersModal
        isOpen={isMethodologyModalOpen}
        onClose={() => setIsMethodologyModalOpen(false)}
      />

      <PageLayout>
        <Header
          title='Comparativa Industria por Calibre y Variedad'
          description='Analiza los precios FOB entre exportadoras que usan CherryMarket.'
        />

        <div className='mb-4'>
          <FiltersToggle
            isOpen={isFilterOpen}
            onToggle={() => setIsFilterOpen(!isFilterOpen)}
            label={isFilterOpen ? 'Ocultar filtros' : 'Mostrar filtros'}
          />
        </div>

        {userProfile?.exporter?.hasDemo && (
          <Alert className='mb-4'>
            Estás viendo una versión de prueba. Contrata CherryMarket para ver
            los datos completos.
          </Alert>
        )}

        {selectedSpecies?.id !== 2 && (
          <Alert variant='danger' className='mb-4'>
            Esta visualización solo está disponible para cerezas. Por favor
            selecciona cereza en el filtro de especies.
          </Alert>
        )}

        <Card
          className={`p-4 ${selectedSpecies?.id !== 2 ? 'opacity-50' : ''}`}
        >
          <CardHeader className='flex justify-between items-center'>
            <h2 className='text-lg font-semibold'>
              FOB por Calibre, Semana {selectedWeek}
            </h2>
            <Button
              size='sm'
              variant='flat'
              startContent={<Icon icon='tabler:download' />}
              onClick={() => exportToExcel('caliber')}
              isDisabled={selectedSpecies?.id !== 2}
            >
              Exportar a Excel
            </Button>
          </CardHeader>
          <CardBody>
            {selectedSpecies?.id !== 2 ? (
              <EmptyState
                title='Esta visualización solo está disponible para cerezas'
                description='Por favor selecciona cereza en el filtro de especies para ver este análisis'
              />
            ) : loading ? (
              <LoadingState />
            ) : (
              <>
                <div className='flex flex-col md:flex-row md:gap-4'>
                  <Select
                    label='Variedad'
                    selectedKeys={selectedVariety ? [selectedVariety] : []}
                    onSelectionChange={(keys) =>
                      setSelectedVariety(Array.from(keys)[0] as string)
                    }
                    className='w-full mb-4'
                    isDisabled={
                      loading ||
                      userProfile?.exporter?.hasDemo ||
                      selectedSpecies?.id !== 2
                    }
                    description={
                      userProfile?.exporter?.hasDemo
                        ? 'Contrata CherryMarket para ver todas las variedades'
                        : ''
                    }
                  >
                    {varieties.map((variety) => (
                      <SelectItem key={variety} value={variety}>
                        {variety}
                      </SelectItem>
                    ))}
                  </Select>
                  <Select
                    label='Semana'
                    selectedKeys={selectedWeek ? [selectedWeek.toString()] : []}
                    onSelectionChange={(keys) => {
                      setSelectedWeek(parseInt(Array.from(keys)[0] as string));
                    }}
                    className='w-full'
                    isDisabled={
                      loading ||
                      userProfile?.exporter?.hasDemo ||
                      selectedSpecies?.id !== 2
                    }
                    description={
                      userProfile?.exporter?.hasDemo
                        ? 'Contrata CherryMarket para ver todas las semanas'
                        : ''
                    }
                  >
                    {weeks.map((week) => (
                      <SelectItem key={week} value={week}>
                        {String(week)}
                      </SelectItem>
                    ))}
                  </Select>
                </div>

                {visualization === 'chart' ? (
                  <ReactApexChart
                    options={getChartOptions(
                      'caliber',
                      ['L', 'XL', 'J', '2J', '3J', '4J', '5J'],
                      priceView,
                      data,
                      selectedWeek,
                      selectedVariety,
                      selectedCaliber,
                      separation
                    )}
                    series={prepareSeriesData('caliber')}
                    type='boxPlot'
                    height={500}
                  />
                ) : (
                  <div className='overflow-x-auto'>
                    <table className='min-w-full border-collapse'>
                      <thead>
                        <tr className='bg-default-100'>
                          <th className='border px-4 py-2'>Estadística</th>
                          {['L', 'XL', 'J', '2J', '3J', '4J', '5J'].map(
                            (caliber) => (
                              <th key={caliber} className='border px-4 py-2'>
                                {caliber}
                              </th>
                            )
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { key: 'own', label: 'FOB Propio' },
                          { key: 'percentile', label: 'Percentil Propio' },
                          { key: 'ownKg', label: 'Kilos Propios' },
                          { key: 'max', label: 'Máximo' },
                          { key: 'q3', label: 'Q3' },
                          { key: 'median', label: 'Mediana' },
                          { key: 'q1', label: 'Q1' },
                          { key: 'min', label: 'Mínimo' },
                          { key: 'othersKg', label: 'Kilos Otras' },
                        ].map(({ key, label }) => {
                          const yearWeek =
                            selectedWeek < 27
                              ? `${
                                  selectedSeason.split('-')[1]
                                }-W${selectedWeek}`
                              : `${
                                  selectedSeason.split('-')[0]
                                }-W${selectedWeek}`;
                          return (
                            <tr
                              key={key}
                              className={
                                ['own', 'percentile', 'ownKg'].includes(key)
                                  ? 'bg-green-50 dark:bg-green-950'
                                  : ''
                              }
                            >
                              <td
                                className={`border px-4 py-2 font-medium ${
                                  ['own', 'percentile', 'ownKg'].includes(key)
                                    ? 'text-green-700 dark:text-green-300'
                                    : ''
                                }`}
                              >
                                {label}
                              </td>
                              {['L', 'XL', 'J', '2J', '3J', '4J', '5J'].map(
                                (caliber) => {
                                  const caliberData =
                                    data[selectedVariety]?.[yearWeek]?.[
                                      caliber
                                    ];
                                  const multiplier =
                                    priceView === 'box' ? 5 : 1;
                                  let value = '-';

                                  if (caliberData) {
                                    if (key === 'own') {
                                      if (
                                        !caliberData.own ||
                                        caliberData.own.length === 0
                                      ) {
                                        value = '-';
                                      } else if (separation === 'none') {
                                        // Use the first value when no separation
                                        value = caliberData.own[0].value
                                          ? `$${(
                                              caliberData.own[0].value *
                                              multiplier
                                            ).toFixed(2)}`
                                          : '-';
                                      } else {
                                        // Join multiple values with their labels/receivers
                                        const values = caliberData.own
                                          .map((item) => {
                                            const label =
                                              separation === 'by_label'
                                                ? item.label
                                                : item.receiver;
                                            return `${label} ${(
                                              item.value * multiplier
                                            ).toFixed(2)}`;
                                          })
                                          .join('\n');
                                        value = values || '-';
                                      }
                                    } else if (key === 'percentile') {
                                      if (
                                        !caliberData.own ||
                                        caliberData.own.length === 0
                                      ) {
                                        value = '-';
                                      } else {
                                        const percentiles = caliberData.own.map(
                                          (item) => {
                                            const percentile =
                                              calculatePercentile(item.value, {
                                                min: caliberData.others.min,
                                                q1: caliberData.others.q1,
                                                median:
                                                  caliberData.others.median,
                                                q3: caliberData.others.q3,
                                                max: caliberData.others.max,
                                              });
                                            const label =
                                              separation === 'by_label'
                                                ? `${item.label} `
                                                : separation === 'by_receiver'
                                                  ? `${item.receiver} `
                                                  : '';
                                            return percentile !== null
                                              ? `${label}${percentile}%`
                                              : '-';
                                          }
                                        );
                                        value = percentiles.join('\n');
                                      }
                                    } else if (key === 'ownKg') {
                                      if (
                                        !caliberData.own ||
                                        caliberData.own.length === 0
                                      ) {
                                        value = '-';
                                      } else {
                                        const kgValues = caliberData.own.map(
                                          (item) => {
                                            const label =
                                              separation === 'by_label'
                                                ? `${item.label} `
                                                : separation === 'by_receiver'
                                                  ? `${item.receiver} `
                                                  : '';
                                            return `${label}${item.kilos?.toLocaleString(
                                              'es-CL'
                                            )} kg`;
                                          }
                                        );
                                        value = kgValues.join('\n');
                                      }
                                    } else if (key === 'othersKg') {
                                      value = caliberData.othersKg
                                        ? caliberData.othersKg.toLocaleString(
                                            'es-CL'
                                          ) + ' kg'
                                        : '-';
                                    } else {
                                      value = caliberData.others[
                                        key as keyof typeof caliberData.others
                                      ]
                                        ? `$${(
                                            caliberData.others[
                                              key as keyof typeof caliberData.others
                                            ] * multiplier
                                          ).toFixed(2)}`
                                        : '-';
                                    }
                                  }

                                  return (
                                    <td
                                      key={`${key}-${caliber}`}
                                      className={`border px-4 py-2 text-center ${
                                        ['own', 'percentile', 'ownKg'].includes(
                                          key
                                        )
                                          ? 'font-semibold text-green-700 dark:text-green-300'
                                          : ''
                                      }`}
                                    >
                                      {value}
                                    </td>
                                  );
                                }
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>

        <Card
          className={`p-4 mt-6 ${
            selectedSpecies?.id !== 2 ? 'opacity-50' : ''
          }`}
        >
          <CardHeader className='flex justify-between items-center'>
            <h2 className='text-lg font-semibold'>
              FOB por Variedad, Semana {selectedWeek}
            </h2>
            <Button
              size='sm'
              variant='flat'
              startContent={<Icon icon='tabler:download' />}
              onClick={() => exportToExcel('variety')}
              isDisabled={selectedSpecies?.id !== 2}
            >
              Exportar a Excel
            </Button>
          </CardHeader>
          <CardBody>
            {selectedSpecies?.id !== 2 ? (
              <EmptyState
                title='Esta visualización solo está disponible para cerezas'
                description='Por favor selecciona cereza en el filtro de especies para ver este análisis'
              />
            ) : loading ? (
              <LoadingState />
            ) : (
              <>
                <div className='flex flex-col md:flex-row md:gap-4'>
                  <Select
                    label='Calibre'
                    selectedKeys={selectedCaliber ? [selectedCaliber] : []}
                    onSelectionChange={(keys) =>
                      setSelectedCaliber(Array.from(keys)[0] as string)
                    }
                    className='w-full mb-4'
                    isDisabled={
                      loading ||
                      userProfile?.exporter?.hasDemo ||
                      selectedSpecies?.id !== 2
                    }
                    description={
                      userProfile?.exporter?.hasDemo
                        ? 'Contrata CherryMarket para ver todos los calibres'
                        : ''
                    }
                  >
                    {['L', 'XL', 'J', '2J', '3J', '4J', '5J'].map((caliber) => (
                      <SelectItem key={caliber} value={caliber}>
                        {caliber}
                      </SelectItem>
                    ))}
                  </Select>
                  <Select
                    label='Semana'
                    selectedKeys={selectedWeek ? [selectedWeek.toString()] : []}
                    onSelectionChange={(keys) => {
                      setSelectedWeek(parseInt(Array.from(keys)[0] as string));
                    }}
                    className='w-full'
                    isDisabled={
                      loading ||
                      userProfile?.exporter?.hasDemo ||
                      selectedSpecies?.id !== 2
                    }
                    description={
                      userProfile?.exporter?.hasDemo
                        ? 'Contrata CherryMarket para ver todas las semanas'
                        : ''
                    }
                  >
                    {weeks.map((week) => (
                      <SelectItem key={week} value={week}>
                        {String(week)}
                      </SelectItem>
                    ))}
                  </Select>
                </div>

                {visualization === 'chart' ? (
                  <ReactApexChart
                    options={getChartOptions(
                      'variety',
                      varieties,
                      priceView,
                      data,
                      selectedWeek,
                      selectedVariety,
                      selectedCaliber,
                      separation
                    )}
                    series={prepareSeriesData('variety')}
                    type='boxPlot'
                    height={500}
                  />
                ) : (
                  <div className='overflow-x-auto'>
                    <table className='min-w-full border-collapse'>
                      <thead>
                        <tr className='bg-default-100'>
                          <th className='border px-4 py-2'>Estadística</th>
                          {varieties.map((variety) => (
                            <th key={variety} className='border px-4 py-2'>
                              {variety}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { key: 'own', label: 'FOB Propio' },
                          { key: 'percentile', label: 'Percentil Propio' },
                          { key: 'ownKg', label: 'Kilos Propios' },
                          { key: 'max', label: 'Máximo' },
                          { key: 'q3', label: 'Q3' },
                          { key: 'median', label: 'Mediana' },
                          { key: 'q1', label: 'Q1' },
                          { key: 'min', label: 'Mínimo' },
                          { key: 'othersKg', label: 'Kilos Otras' },
                        ].map(({ key, label }) => {
                          const yearWeek =
                            selectedWeek < 27
                              ? `${
                                  selectedSeason.split('-')[1]
                                }-W${selectedWeek}`
                              : `${
                                  selectedSeason.split('-')[0]
                                }-W${selectedWeek}`;
                          return (
                            <tr
                              key={key}
                              className={
                                ['own', 'percentile', 'ownKg'].includes(key)
                                  ? 'bg-green-50 dark:bg-green-950'
                                  : ''
                              }
                            >
                              <td
                                className={`border px-4 py-2 font-medium ${
                                  ['own', 'percentile', 'ownKg'].includes(key)
                                    ? 'text-green-700 dark:text-green-300'
                                    : ''
                                }`}
                              >
                                {label}
                              </td>
                              {varieties.map((variety) => {
                                const varietyData =
                                  data[variety]?.[yearWeek]?.[selectedCaliber];
                                const multiplier = priceView === 'box' ? 5 : 1;
                                let value = '-';

                                if (varietyData) {
                                  if (key === 'own') {
                                    if (
                                      !varietyData.own ||
                                      varietyData.own.length === 0
                                    ) {
                                      value = '-';
                                    } else if (separation === 'none') {
                                      // Use the first value when no separation
                                      value = varietyData.own[0].value
                                        ? `$${(
                                            varietyData.own[0].value *
                                            multiplier
                                          ).toFixed(2)}`
                                        : '-';
                                    } else {
                                      // Join multiple values with their labels/receivers
                                      const values = varietyData.own
                                        .map((item) => {
                                          const label =
                                            separation === 'by_label'
                                              ? item.label
                                              : item.receiver;
                                          return `${label} ${(
                                            item.value * multiplier
                                          ).toFixed(2)}`;
                                        })
                                        .join('\n');
                                      value = values || '-';
                                    }
                                  } else if (key === 'percentile') {
                                    const percentiles = varietyData.own.map(
                                      (item) => {
                                        const percentile = calculatePercentile(
                                          item.value,
                                          {
                                            min: varietyData.others.min,
                                            q1: varietyData.others.q1,
                                            median: varietyData.others.median,
                                            q3: varietyData.others.q3,
                                            max: varietyData.others.max,
                                          }
                                        );
                                        const label =
                                          separation === 'by_label'
                                            ? `${item.label} `
                                            : separation === 'by_receiver'
                                              ? `${item.receiver} `
                                              : '';
                                        return percentile !== null
                                          ? `${label}${percentile}%`
                                          : '-';
                                      }
                                    );
                                    value = percentiles.join('\n');
                                  } else if (key === 'ownKg') {
                                    const kgValues = varietyData.own.map(
                                      (item) => {
                                        const label =
                                          separation === 'by_label'
                                            ? `${item.label} `
                                            : separation === 'by_receiver'
                                              ? `${item.receiver} `
                                              : '';
                                        return `${label}${item.kilos?.toLocaleString(
                                          'es-CL'
                                        )} kg`;
                                      }
                                    );
                                    value = kgValues.join('\n');
                                  } else if (key === 'othersKg') {
                                    value = varietyData.othersKg
                                      ? varietyData.othersKg.toLocaleString(
                                          'es-CL'
                                        ) + ' kg'
                                      : '-';
                                  } else {
                                    value = varietyData.others[
                                      key as keyof typeof varietyData.others
                                    ]
                                      ? `$${(
                                          varietyData.others[
                                            key as keyof typeof varietyData.others
                                          ] * multiplier
                                        ).toFixed(2)}`
                                      : '-';
                                  }
                                }

                                return (
                                  <td
                                    key={`${key}-${variety}`}
                                    className={`border px-4 py-2 text-center ${
                                      ['own', 'percentile', 'ownKg'].includes(
                                        key
                                      )
                                        ? 'font-semibold text-green-700 dark:text-green-300'
                                        : ''
                                    }`}
                                  >
                                    {value}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </PageLayout>
    </div>
  );
};

export default SettlementsComparativeExporters;
